import React from 'react';
import {
    ListGroup,
    ListGroupItem,
    ListGroupItemHeading,
    ListGroupItemText,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCogs,
    faHeadset,
    faStar,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import authService from '../api-authorization/AuthorizeService';
import { SmallButton } from '../common/forms/FormElements';
import CustomCircularProgress from '../common/CustomCircularProgress';
import {
    getSupportToolRegistryItem,
    StandaloneSupportTools,
    SupportToolAdminToolIds,
} from './supportTools.registry';

const favouriteSupportToolIds = [10];

const renderSupportTools = (adminTools, origin) => {
    const getSupportToolPath = (adminToolId) => {
        if (origin === 'admin') {
            return adminToolId ? `${AppNavPaths.AdminTool}/${adminToolId}` : AppNavPaths.AdminTool;
        }
        const toolId = getSupportToolRegistryItem(adminToolId, 'adminToolId')?.id;
        return toolId ? `${AppNavPaths.SupportTools}/${toolId}` : AppNavPaths.SupportTools;
    };

    // Database-backed tools first, then the registry-only ones. Standalone tools have no AdminTool
    // row, so they exist only on the Support Tools page (Craig, 2026-09-04) — never under Admin Tools.
    const items = [
        ...adminTools.map((tool) => ({
            key: `admin-tool-${tool.id}`,
            to: getSupportToolPath(tool.id),
            name: tool.toolName,
            description: tool.description,
            favourite: favouriteSupportToolIds.includes(tool.id),
        })),
        ...(origin === 'support'
            ? StandaloneSupportTools.map((tool) => ({
                key: tool.id,
                to: `${AppNavPaths.SupportTools}/${tool.id}`,
                name: tool.name,
                description: tool.description,
                favourite: false,
            }))
            : []),
    ];

    return (
        <div className="support-tools-index bg-light w-100 overflow-auto">
            <ListGroup className="mb-4">
                <h4>Support Tools</h4>
                {items.map((item) => (
                    <Link
                        component={ListGroupItem}
                        tag="a"
                        to={item.to}
                        action
                        key={item.key}
                    >
                        <ListGroupItemHeading>
                            {item.favourite && (
                                <FontAwesomeIcon
                                    icon={faStar}
                                    className="mr-2 text-warning"
                                />
                            )}
                            {item.name}
                        </ListGroupItemHeading>
                        <ListGroupItemText className="mb-2">
                            {item.description}
                        </ListGroupItemText>
                    </Link>
                ))}
            </ListGroup>
        </div>
    );
};

class AdminToolIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = { loading: true };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    getAdminTool = async () => {
        this.setState({ loading: true });

        const [adminToolList] = await Promise.all([
            httpClient.js(ApiRoutes.adminTool.search()),
        ]);

        this.setState({
            loading: false,
            adminToolList,
        });
    };

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();
        const tenant = await httpClient.js(ApiRoutes.tenant.settings());

        if (isAuthenticated) {
            this.setState({ loading: true });
            this.getAdminTool();

            if (tenant && tenant.id) {
                this.setState({ tenantId: tenant.id });
            }
        }
    }

    render() {
        const { supportOnly = false } = this.props;
        const adminToolList = this.state.adminToolList ?? [];
        const supportToolCategories = adminToolList
            .map((category) => ({
                ...category,
                adminTools: (category.adminTools ?? []).filter(
                    (item) => SupportToolAdminToolIds.includes(item.id),
                ),
            }))
            .filter((category) => category.adminTools.length > 0);
        const supportAdminTools = supportToolCategories.flatMap(
            (category) => category.adminTools,
        );
        const nonSupportToolCategories = adminToolList
            .map((category) => ({
                ...category,
                adminTools: (category.adminTools ?? []).filter(
                    (item) => !SupportToolAdminToolIds.includes(item.id),
                ),
            }))
            .filter((category) => category.adminTools.length > 0);

        if (this.state.loading) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={supportOnly ? 'Loading support tools...' : 'Loading admin tools...'}
                    />
                </div>
            );
        }

        if (!(this.context ?? {}).user) return null;

        if (supportOnly) {
            return (
                <div className="w-100 p-5 bg-light">
                    <div className="support-tools-header mb-4">
                        <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon
                                icon={faHeadset}
                                className="mr-3 text-primary"
                                size="2x"
                            />
                            <div>
                                <h2 className="mb-0">Support Tools</h2>
                                <p className="text-muted mb-0">
                                    Tools for the support team to help resolve common customer issues
                                </p>
                            </div>
                        </div>
                    </div>
                    {renderSupportTools(supportAdminTools, 'support')}
                </div>
            );
        }

        return (
            <div className="w-100">
                <div className="p-5 bg-light w-100">
                    <h2>
                        Admin Tools
                    </h2>

                    <Link
                        component={SmallButton}
                        type="button"
                        className="mb-4"
                        to={`${AppNavPaths.Tenant}/${this.state.tenantId}`}
                    >
                        <FontAwesomeIcon
                            icon={faCogs}
                            className="mr-2"
                        />
                        Tenant Settings
                    </Link>
                    {renderSupportTools(supportAdminTools, 'admin')}
                    <div>
                        <div>
                            {nonSupportToolCategories.map((category) => (
                                <ListGroup
                                    key={category.id}
                                    className="mb-4"
                                >
                                    <h4>{category.description}</h4>

                                    {category.adminTools.map((item) => (
                                        <Link
                                            component={ListGroupItem}
                                            tag="a"
                                            to={`${AppNavPaths.AdminTool}/${item.id}`}
                                            action
                                            key={item.id}
                                        >
                                            <ListGroupItemHeading>
                                                {Boolean(item.isFavourite) && (
                                                <FontAwesomeIcon
                                                    icon={faStar}
                                                    className="mr-2 text-warning"
                                                />
                                            )}

                                                {item.toolName}
                                            </ListGroupItemHeading>

                                            <ListGroupItemText className="mb-0">
                                                {item.description}
                                            </ListGroupItemText>
                                        </Link>
                                ))}
                                </ListGroup>
                        ))}
                        </div>
                    </div>
                </div>

            </div>
        );
    }
}

export default AdminToolIndex;
